import { ApiError, apiFetch, API_BASE } from "@/client/lib/api";
import type { AuditMetadataFilter, AuditSearch, AuditSearchResponse } from "@/shared/audit";

export type AuditSearchInput = Partial<Omit<AuditSearch, "metadata">> & {
  metadata?: AuditMetadataFilter[];
};

export function auditSearchParams(input: AuditSearchInput): URLSearchParams {
  const params = new URLSearchParams();
  const keys: Array<Exclude<keyof AuditSearchInput, "metadata">> = [
    "q",
    "id",
    "tenantScope",
    "tenantId",
    "actor",
    "actorKind",
    "action",
    "targetType",
    "targetId",
    "ip",
    "securityReview",
    "from",
    "to",
    "order",
    "cursor",
    "limit",
  ];
  for (const key of keys) {
    const value = input[key];
    if (
      value !== undefined &&
      value !== "" &&
      value !== false &&
      !(key === "tenantScope" && value === "all")
    ) {
      params.set(key, String(value));
    }
  }
  const metadata = input.metadata?.filter((filter) => filter.key.trim());
  if (metadata && metadata.length > 0) {
    params.set("metadata", JSON.stringify(metadata));
  }
  return params;
}

async function exportAuditCsv(input: AuditSearchInput): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(
    `${API_BASE}/audit/export.csv?${auditSearchParams({ ...input, cursor: undefined })}`,
    {
      credentials: "include",
      headers: { Accept: "text/csv" },
    },
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      code?: string;
    } | null;
    throw new ApiError(
      response.status,
      body?.error ?? `Export failed (${response.status})`,
      body?.code,
    );
  }
  const disposition = response.headers.get("content-disposition");
  const filename = disposition?.match(/filename="([^"]+)"/)?.[1] ?? "portal-audit.csv";
  return { blob: await response.blob(), filename };
}

export const auditAdminApi = {
  search: (input: AuditSearchInput, signal?: AbortSignal) =>
    apiFetch<AuditSearchResponse>(`/audit?${auditSearchParams(input)}`, { signal }),
  exportCsv: exportAuditCsv,
};
